import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import SelectInput from "../../components/common/inputs/selectInput";
import ModalManager from "../../components/common/modal/ModalManager";
import TextInput from "../../components/common/inputs/textInput";
import Loading from "../../components/common/loading";
import InfoPopover from "../../components/common/infoPopover";
import ReactPaginate from "react-paginate";
import useDarkMode from "../../hooks/useDarkMode";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Tooltip,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Legend,
  Filler,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { AppCard, AppPage, AppPageHeader, AppToolbar, AppToolbarField } from "../../components/common/appShell";
ChartJS.register(
  Tooltip,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Legend,
  Filler,
);

const ChartLoadingPanel = ({ dark }) => (
  <div
    role="status"
    aria-live="polite"
    style={{
      minHeight: "285px",
      border: `1px solid ${dark ? "rgba(148, 163, 184, 0.2)" : "#e5e7eb"}`,
      borderRadius: "6px",
      background: dark ? "rgba(15, 23, 42, 0.28)" : "#f8fafc",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: "14px",
      padding: "28px",
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
      <div className="skeleton-blank" style={{ width: "180px", height: "16px", margin: 0 }} />
      <div className="skeleton-blank" style={{ width: "120px", height: "16px", margin: 0 }} />
    </div>
    <div className="skeleton-blank" style={{ width: "100%", height: "140px", margin: 0 }} />
    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
      <div className="skeleton-blank" style={{ width: "34%", height: "12px", margin: 0 }} />
      <div className="skeleton-blank" style={{ width: "24%", height: "12px", margin: 0 }} />
    </div>
    <span style={{ color: "var(--app-text-muted)", fontSize: "13px" }}>
      Loading power history...
    </span>
  </div>
);

export default class DeviceInlets extends React.Component {
  state = {
    selectedDevices: [],
    selectedGroup: "",
    selectedGroupId: "",
    editingActive: false,

    searchBar: "",
    currentFilter: "",
    trendRangeKey: "24h",
    trendMetricKey: "current",
    trendRows: [],
    trendMeta: {},
    trendsLoading: false,
    trendsLoaded: false,
    trendsError: "",
  };
  setActiveInletGroup = (groupId) => {
    const foundGroup =
      this.props.resources.groups.find((g) => g.id === groupId) || {};
    const foundDeviceIds = (foundGroup.items || []).map((i) => ({
      enclosureSerialNumber: parseInt(i, 10),
    }));
    this.setState({
      selectedDevices: foundDeviceIds,
      selectedGroup: foundGroup.name,
      selectedGroupId: foundGroup.id,
    });
  };
  applyFilters = () => {
    this.setState({
      currentFilter: this.state.searchBar
    }, () => {
      this.fetchInletsPage(1, true);
      this.fetchInletTrends();
    })
  }
  fetchInletsPage = (page = 1, resetPages = true) => {
    const queryParams = {
      page,
      search: this.state.currentFilter,
      ...(this.props.queryParams || {}),
      groupBy: "device",
    };
    this.props.fetchResourcePaginate("inlets", undefined, queryParams, resetPages);
  };
  buildTrendQueryString = () => {
    const range = buildTrendRange(this.state.trendRangeKey);
    const params = new URLSearchParams();
    Object.entries({
      ...(this.props.queryParams || {}),
      search: this.state.currentFilter,
      since: range.since,
      before: range.before,
      bucketMinutes: range.bucketMinutes,
    }).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    return params.toString();
  };
  fetchInletTrends = () => {
    if (this.inletTrendsCancelSource) {
      this.inletTrendsCancelSource.cancel("Canceling stale inlet trend request");
    }

    const cancelSource = axios.CancelToken.source();
    this.inletTrendsCancelSource = cancelSource;
    this.setState({ trendsLoading: true, trendsError: "" });

    axios
      .get(`/v2/inlets/trends?${this.buildTrendQueryString()}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        cancelToken: cancelSource.token,
      })
      .then((res) => {
        if (this.inletTrendsCancelSource !== cancelSource) return;
        this.setState({
          trendRows: res.data?.data || [],
          trendMeta: res.data?.meta || {},
          trendsLoaded: true,
        });
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.log(error);
        if (this.inletTrendsCancelSource !== cancelSource) return;
        this.setState({
          trendRows: [],
          trendMeta: {},
          trendsLoaded: true,
          trendsError: "Unable to load inlet trends.",
        });
      })
      .finally(() => {
        if (this.inletTrendsCancelSource === cancelSource) {
          this.setState({ trendsLoading: false });
        }
      });
  };
  setTrendRange = (trendRangeKey) => {
    this.setState({ trendRangeKey }, () => this.fetchInletTrends());
  };
  setTrendMetric = (trendMetricKey) => {
    this.setState({ trendMetricKey });
  };
  refreshTrends = () => {
    this.fetchInletTrends();
  };
  removeGroup = (groupId) => {
    ModalManager.confirm(
      "This action will permanently delete this group.",
      "Remove Group",
      "Remove",
      (submitted, close) => {
        if (submitted) {
          this.props.removeResource("groups", groupId, (err, res) => {
            this.setState({
              selectedDevices: [],
              selectedGroup: "",
              selectedGroupId: "",
              editingActive: false,
            });
            close();
          });
        } else {
          // close();
        }
      }
    );
  };
  createGroup = () => {
    const items = this.state.selectedDevices.map(
      (d) => d.enclosureSerialNumber
    );
    ModalManager.confirm(
      "",
      "Create New Group",
      "Create",
      (status, close) => {
        if (status) {
          close();
        }
      },
      ({ close }) => (
        <InletGroupForm
          close={close}
          items={items}
          createResource={this.props.createResource}
          changeAppState={(newState) => this.setState(newState)}
        />
      ), // body component
      true,
      true
    );
  };
  updateGroup = (groupId) => {
    const items = this.state.selectedDevices.map(
      (d) => d.enclosureSerialNumber
    );
    this.props.modifyResource(
      "groups",
      groupId,
      {
        items,
      },
      (err, res) => {
        if (!err && res) {
          this.setState({ editingActive: false });
        } else {
          console.log("err occurred");
        }
      }
    );
  };
  componentDidMount() {
    if (!this.props.resources.groupsLoaded && !this.props.resources.groupsLoading) {
      this.props.fetchResource("groups");
    }
    this.fetchInletsPage(1, true);
    this.fetchInletTrends();

  }
  componentDidUpdate(prevProps) {
    const previousGroupId = prevProps.match?.params?.groupId;
    const currentGroupId = this.props.match?.params?.groupId;
    if (previousGroupId !== currentGroupId) {
      this.fetchInletsPage(1, true);
      this.fetchInletTrends();
    }
  }
  componentWillUnmount() {
    if (this.inletTrendsCancelSource) {
      this.inletTrendsCancelSource.cancel("Canceling inlet trend request");
      this.inletTrendsCancelSource = null;
    }
  }
  render() {
    const columnWidths = [
      1, //0 toggle
      1, //1 status icon
      5, //1 plug
      4, //3 PDu name
      2, //4 current
      2, //5 voltage
      2, //6 line freq
      2, //7 power factor
      2, //8 active pwoer
      3, //9 energy
      2, //10 last update
      1, //11 refresh
    ];
    const { inletsLoading, inletsLoaded } =
      this.props.resources;
    const inletsMeta = this.props.resources.inletsMeta || {};
    const summary = inletsMeta.summary || {};
    const pageLoading = this.props.resources.inletsPageLoading || {};
    const isLoadingPage = Object.values(pageLoading).some(Boolean);

    return (
      <AppPage>
        <AppPageHeader
          title="Inlets"
          description="Review inlet power readings, compare connected devices, and search by device name, inlet plug, or serial number."
        />
        <Chartz
          summary={summary}
          loading={isLoadingPage && !inletsLoaded}
          loaded={inletsLoaded}
        />
        <InletTrendsPanel
          rows={this.state.trendRows}
          meta={this.state.trendMeta}
          loading={this.state.trendsLoading}
          loaded={this.state.trendsLoaded}
          error={this.state.trendsError}
          rangeKey={this.state.trendRangeKey}
          metricKey={this.state.trendMetricKey}
          onRangeChange={this.setTrendRange}
          onMetricChange={this.setTrendMetric}
          onRefresh={this.refreshTrends}
        />
        <AppCard
          variant="hybrid-list"
          // title="Inlet inventory"
          // description="Review inlet plug details, live readings, and the device tied to each inlet."
        >
        <AppToolbar>
          <AppToolbarField label="Search inlets">
            <div className="app-toolbar__search">
                <input
                  type="text"
                  placeholder="Search by device name, inlet plug, or serial number"
                  value={this.state.searchBar}
                  onChange={(e) => {
                    this.setState({ searchBar: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      this.applyFilters();
                    }
                  }}
                />
                <span
                  className="search-icon-button"
                  onClick={() => {
                    this.applyFilters();
                  }}
                >
                  <i className="material-icons">search</i>
                </span>
              </div>
          </AppToolbarField>
        </AppToolbar>
        <div
          style={{
            overflowX: "auto",
          }}
        >
          <div className="grid-row header">
            <div style={{ flex: columnWidths[2] }}>Plug</div>
            <div style={{ flex: columnWidths[3] }}>PDU</div>
            <div style={{ flex: columnWidths[4] }}>Current</div>
            <div style={{ flex: columnWidths[5] }}>Voltage</div>
            <div style={{ flex: columnWidths[6] }}>Line Freq</div>
            <div style={{ flex: columnWidths[7] }}>Power Factor</div>
            <div style={{ flex: columnWidths[8] }}>Active Power</div>
            <div style={{ flex: columnWidths[9] }}>Energy Consumption</div>
            {/* <div style={{ flex: columnWidths[10] }}>Last Update</div> */}
            <div style={{ flex: columnWidths[11] }}></div>
          </div>
          {!this.props.resources && (
            <LoadingSection columnWidths={columnWidths} />
          )}

          {/* {this.props.resources.devicesLoaded && devices.length === 0 && (
            <React.Fragment>
              <div className="grid-row-wrapper">
                <div className="grid-row">
                  <div
                    style={{
                      paddingLeft: "50px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>No Inlets Found</div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  paddingTop: "40px",
                }}
              >
                <span className="button">
                  <a
                    href="https://synaccess.readme.io/reference/connect-a-synlink-pdu"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "white" }}
                  >
                    Learn how to connect a SynLink PDU
                  </a>
                </span>
              </div>
            </React.Fragment>
          )} */}
          <PaginatedList
            resourceName="inlets"
            pages={this.props.resources.inletsPages || []}
            pageLoading={pageLoading}
            fetchResourcePaginate={this.props.fetchResourcePaginate}
            totalItemCount={
              typeof inletsMeta.totalCount === "number"
                ? inletsMeta.totalCount
                : undefined
            }
            showSummary
            itemLabel="PDUs"
            queryParams={{
              search: this.state.currentFilter,
              ...(this.props.queryParams || {}),
              groupBy: "device",
            }}
            resetKey={this.props.resources.inletsPaginationResetKey}
            LoadingProp={() => <LoadingSection columnWidths={columnWidths} />}
            parentProps={{
              columnWidths,
              inletsLoading,
              inletsLoaded,
            }}
            ItemRenderer={InletTableRow}
            renderHeader={() => (
              <>
                {this.props.resources.inletsLoading &&
                  this.props.resources.inlets.length === 0 && (
                    <LoadingSection columnWidths={columnWidths} />
                  )}
              </>
            )}
          />
          {/* {filteredDevices.map((device) => {})} */}
        </div>
        </AppCard>
      </AppPage>
    );
  }
}

const hasNumber = (value) => value !== undefined && value !== null && Number.isFinite(Number(value));

const formatNumber = (value, digits = 2) => {
  const number = Number(value || 0);
  return number
    .toFixed(digits)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const TREND_RANGE_OPTIONS = [
  { key: "24h", label: "24 hours", durationMs: 24 * 60 * 60 * 1000, bucketMinutes: 15 },
  { key: "7d", label: "7 days", durationMs: 7 * 24 * 60 * 60 * 1000, bucketMinutes: 120 },
  { key: "30d", label: "30 days", durationMs: 30 * 24 * 60 * 60 * 1000, bucketMinutes: 720 },
];

const TREND_METRICS = {
  current: {
    key: "totalCurrentRms",
    label: "Total current",
    unit: "A",
    digits: 2,
    color: "#2563eb",
  },
  power: {
    key: "totalActivePower",
    label: "Active power",
    unit: "W",
    digits: 0,
    color: "#047857",
  },
  voltage: {
    key: "avgVoltageRms",
    label: "Average voltage",
    unit: "V",
    digits: 1,
    color: "#b45309",
  },
};

const TREND_TIME_DISPLAY_FORMATS = {
  minute: "MMM d, h:mm a",
  hour: "MMM d, h a",
  day: "MMM d",
  month: "MMM yyyy",
};

const buildTrendRange = (rangeKey) => {
  const option = TREND_RANGE_OPTIONS.find((item) => item.key === rangeKey) || TREND_RANGE_OPTIONS[0];
  const before = Date.now();
  return {
    since: before - option.durationMs,
    before,
    bucketMinutes: option.bucketMinutes,
  };
};

const resolveTrendTimeUnit = (durationMs) => {
  const hours = durationMs / 3600000;
  if (!Number.isFinite(hours) || hours <= 0) return "hour";
  if (hours <= 36) return "hour";
  if (hours <= 24 * 75) return "day";
  return "month";
};

const withAlpha = (color, alpha) => {
  if (!color || !color.startsWith("#")) return `rgba(37, 99, 235, ${alpha})`;
  const hex = color.slice(1);
  if (hex.length !== 6) return color;
  const value = parseInt(hex, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatTrendMetric = (value, metric) => {
  if (!hasNumber(value)) return "-";
  return `${formatNumber(value, metric.digits)}${metric.unit ? ` ${metric.unit}` : ""}`;
};

const formatTrendCount = (value) => {
  if (!hasNumber(value)) return "-";
  return formatNumber(value, 0);
};

const formatTrendPercent = (value) => {
  if (!hasNumber(value)) return "-";
  const number = Number(value);
  const sign = number > 0 ? "+" : "";
  return `${sign}${formatNumber(number, 1)}%`;
};

const formatTrendDateRange = (since, before) => {
  const options = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return `${new Date(since).toLocaleString(undefined, options)} - ${new Date(before).toLocaleString(undefined, options)}`;
};

const formatBucketDuration = (minutes) => {
  const numericMinutes = Number(minutes);
  if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) return "latest";
  if (numericMinutes < 60) return `${numericMinutes} min`;
  const hours = numericMinutes / 60;
  return Number.isInteger(hours) ? `${hours} hr` : `${formatNumber(hours, 1)} hr`;
};

const expectedLoadLabel = (expected = {}, anomaly = {}) => {
  if (expected.status !== "ready") return "Learning";
  if (anomaly.state === "near_expected") return "Near expected";
  if (anomaly.state === "low_load") return "Low load";
  if (anomaly.state === "high_load") return "High load";
  if (anomaly.state === "normal") return "Normal";
  return "Learning";
};

const expectedLoadColor = (expected = {}, anomaly = {}) => {
  if (expected.status !== "ready") return "#6b7280";
  if (anomaly.state === "normal") return "#047857";
  if (anomaly.state === "near_expected") return "var(--app-text-muted)";
  if (anomaly.severity === "critical") return "#b91c1c";
  if (anomaly.state === "low_load" || anomaly.state === "high_load") return "#b45309";
  return "#6b7280";
};

const expectedRangeSubValue = (expected = {}) => {
  if (
    expected.status === "ready" &&
    hasNumber(expected.currentP10) &&
    hasNumber(expected.currentP90)
  ) {
    return `Expected ${formatNumber(expected.currentP10, 2)}-${formatNumber(expected.currentP90, 2)} A`;
  }
  if (expected.status === "too_many_channels") return "Too many channels";
  if (expected.status === "no_channels") return "No channels scoped";
  return "Need more history";
};

const expectedRangeInfo = (expected = {}, anomaly = {}, latestCurrent = null) => {
  if (expected.status !== "ready") {
    return (
      <div style={{ lineHeight: 1.45 }}>
        <div>Expected range needs more profile history.</div>
        <div style={{ marginTop: "0.65rem" }}>
          Learned from the last 30 days for the same UTC hour of week.
        </div>
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.45 }}>
      <div>Expected range: {formatNumber(expected.currentP10, 2)}-{formatNumber(expected.currentP90, 2)} A</div>
      <div>Expected median: {formatNumber(expected.currentMedian, 2)} A</div>
      <div>Latest current: {latestCurrent !== null ? `${formatNumber(latestCurrent, 2)} A` : "-"}</div>
      {anomaly.durationMinutes > 0 ? (
        <div>Duration: {formatBucketDuration(anomaly.durationMinutes)}</div>
      ) : null}
      {anomaly.message ? (
        <div style={{ marginTop: "0.65rem" }}>{anomaly.message}</div>
      ) : null}
      <div style={{ marginTop: "0.65rem" }}>
        Learned from the last 30 days for the same UTC hour of week.
      </div>
    </div>
  );
};

const buildExpectedPointSet = (rows, field) =>
  rows
    .map((row) => {
      const x = row.createdAt ? new Date(row.createdAt) : null;
      if (!x || Number.isNaN(x.getTime())) return null;
      return {
        x,
        y: hasNumber(row[field]) ? Number(row[field]) : null,
      };
    })
    .filter(Boolean);

const buildExpectedCurrentDatasets = (rows) => {
  const hasExpectedRange = rows.some(
    (row) => hasNumber(row.expectedCurrentP10) && hasNumber(row.expectedCurrentP90),
  );
  if (!hasExpectedRange) return [];

  const rangeColor = "#64748b";
  return [
    {
      label: "Expected lower",
      data: buildExpectedPointSet(rows, "expectedCurrentP10"),
      borderColor: "rgba(100, 116, 139, 0)",
      backgroundColor: "rgba(100, 116, 139, 0)",
      fill: false,
      pointRadius: 0,
      borderWidth: 0,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
      hideFromLegend: true,
    },
    {
      label: "Expected range",
      data: buildExpectedPointSet(rows, "expectedCurrentP90"),
      borderColor: withAlpha(rangeColor, 0.35),
      backgroundColor: withAlpha(rangeColor, 0.14),
      fill: "-1",
      pointRadius: 0,
      borderWidth: 1,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
    },
    {
      label: "Expected median",
      data: buildExpectedPointSet(rows, "expectedCurrentMedian"),
      borderColor: withAlpha(rangeColor, 0.8),
      borderDash: [4, 4],
      backgroundColor: "rgba(100, 116, 139, 0)",
      fill: false,
      pointRadius: 0,
      borderWidth: 1.5,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
    },
  ];
};

const InletTrendsPanel = ({
  rows = [],
  meta = {},
  loading,
  loaded,
  error,
  rangeKey,
  metricKey,
  onRangeChange,
  onMetricChange,
  onRefresh,
}) => {
  const dark = useDarkMode();
  const metric = TREND_METRICS[metricKey] || TREND_METRICS.current;
  const fallbackRange = buildTrendRange(rangeKey);
  const parsedSince = Date.parse(meta.since);
  const parsedBefore = Date.parse(meta.before);
  const since = Number.isFinite(parsedSince) ? parsedSince : fallbackRange.since;
  const before = Number.isFinite(parsedBefore) ? parsedBefore : fallbackRange.before;
  const durationMs = Math.max(1, before - since);
  const latest = meta.latest || {};
  const expectedPduCount = Number(meta.expectedPduCount || 0);
  const reportingPduCount = Number(latest.reportingPduCount || 0);
  const reportingPercent = expectedPduCount
    ? Math.round((reportingPduCount / expectedPduCount) * 100)
    : 0;
  const peakActivePower = rows.reduce((peak, row) => {
    const activePower = hasNumber(row.totalActivePower) ? Number(row.totalActivePower) : null;
    if (activePower === null) return peak;
    return peak === null ? activePower : Math.max(peak, activePower);
  }, null);
  const baselineCurrent = meta.baseline && hasNumber(meta.baseline.totalCurrentRms)
    ? Number(meta.baseline.totalCurrentRms)
    : null;
  const latestCurrent = hasNumber(latest.totalCurrentRms) ? Number(latest.totalCurrentRms) : null;
  const bucketMinutes = Number(meta.bucketMinutes || fallbackRange.bucketMinutes);
  const currentChange = hasNumber(meta.currentChangePct) ? Number(meta.currentChangePct) : null;
  const expected = meta.expected || {};
  const anomaly = meta.anomaly || {};
  const expectedDatasets = metricKey === "current" ? buildExpectedCurrentDatasets(rows) : [];
  const changeColor = currentChange === null
    ? "#6b7280"
    : currentChange < -20
      ? "#b91c1c"
      : currentChange < 0
        ? "#b45309"
        : "#047857";
  const loadChangeDirection = currentChange === null
    ? "Not enough current data is available for this timeframe."
    : currentChange < 0
      ? "Current is lower than the earlier average for this timeframe."
      : currentChange > 0
        ? "Current is higher than the earlier average for this timeframe."
        : "Current matches the earlier average for this timeframe.";
  const loadChangeInfo = (
    <div style={{ lineHeight: 1.45 }}>
      <div>Latest {formatBucketDuration(bucketMinutes)} avg: {latestCurrent !== null ? `${formatNumber(latestCurrent, 2)} A` : "-"}</div>
      <div>Earlier avg: {baselineCurrent !== null ? `${formatNumber(baselineCurrent, 2)} A` : "-"}</div>
      <div>Change: {formatTrendPercent(meta.currentChangePct)}</div>
      <div style={{ marginTop: "0.65rem" }}>{loadChangeDirection}</div>
    </div>
  );

  const chartData = {
    datasets: [
      ...expectedDatasets,
      {
        label: metric.label,
        data: rows
          .map((row) => {
            const x = row.createdAt ? new Date(row.createdAt) : null;
            if (!x || Number.isNaN(x.getTime())) return null;
            return {
              x,
              y: hasNumber(row[metric.key]) ? Number(row[metric.key]) : null,
            };
          })
          .filter(Boolean),
        borderColor: metric.color,
        backgroundColor: withAlpha(metric.color, 0.16),
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
        tension: 0.18,
        spanGaps: false,
        parsing: false,
      },
    ],
  };

  const chartTheme = {
    axis: dark ? "#a8b3c2" : "#64748b",
    grid: dark ? "rgba(148, 163, 184, 0.14)" : "rgba(15, 23, 42, 0.08)",
    tooltipBg: dark ? "rgba(15, 23, 42, 0.96)" : "rgba(15, 23, 42, 0.92)",
    tooltipText: "#f8fafc",
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: chartTheme.axis,
          filter: (item, data) => !data.datasets[item.datasetIndex]?.hideFromLegend,
        },
      },
      tooltip: {
        backgroundColor: chartTheme.tooltipBg,
        bodyColor: chartTheme.tooltipText,
        titleColor: chartTheme.tooltipText,
        borderColor: chartTheme.grid,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatTrendMetric(context.parsed?.y, metric)}`,
        },
      },
    },
    scales: {
      x: {
        type: "time",
        min: since,
        max: before,
        time: {
          tooltipFormat: "PP p",
          unit: resolveTrendTimeUnit(durationMs),
          displayFormats: TREND_TIME_DISPLAY_FORMATS,
        },
        ticks: { autoSkip: true, color: chartTheme.axis, maxRotation: 0 },
        grid: { color: chartTheme.grid, display: false },
      },
      y: {
        ticks: {
          color: chartTheme.axis,
          callback: (value) => formatTrendMetric(value, metric),
        },
        grid: { color: chartTheme.grid },
      },
    },
  };

  const selectStyle = {
    margin: 0,
    width: "100%",
    padding: "0.35rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid var(--app-control-border)",
    backgroundColor: "var(--app-control-bg)",
    color: "var(--app-control-text)",
  };

  const trendControlGridStyle = {
    alignItems: "end",
    display: "grid",
    gap: "0.85rem",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    marginBottom: "1rem",
  };

  const trendControlLabelStyle = {
    color: "var(--text-muted-color, #6b7280)",
    display: "flex",
    flexDirection: "column",
    fontSize: "0.78rem",
    fontWeight: 700,
    gap: "0.35rem",
  };

  return (
    <AppCard title="Power trends">
      <div className="app-card__section" style={{ paddingTop: 15 }}>
        <div style={trendControlGridStyle}>
          <label style={trendControlLabelStyle}>
            <span>Metric</span>
            <select value={metricKey} onChange={(event) => onMetricChange(event.target.value)} style={selectStyle}>
              {Object.entries(TREND_METRICS).map(([key, item]) => (
                <option key={key} value={key}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label style={trendControlLabelStyle}>
            <span>Timeframe</span>
            <select value={rangeKey} onChange={(event) => onRangeChange(event.target.value)} style={selectStyle}>
              {TREND_RANGE_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div>
            <button
              type="button"
              className="button secondary app-toolbar__action-button"
              onClick={onRefresh}
              style={{
                justifyContent: "center",
                margin: 0,
                minHeight: "40px",
                width: "100%",
              }}
            >
              <i className="material-icons" style={{ fontSize: "18px" }}>refresh</i>
              <span>Refresh</span>
            </button>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <TrendStat
            label="Current now"
            value={formatTrendMetric(latest.totalCurrentRms, TREND_METRICS.current)}
          />
          <TrendStat
            label="Load change"
            value={formatTrendPercent(meta.currentChangePct)}
            color={changeColor}
            subValue={
              meta.loadDropDetected
                ? "Drop detected"
                : baselineCurrent !== null
                  ? `Baseline ${formatNumber(baselineCurrent, 2)} A`
                  : "Vs baseline"
            }
            info={loadChangeInfo}
          />
          <TrendStat
            label="Load state"
            value={expectedLoadLabel(expected, anomaly)}
            color={expectedLoadColor(expected, anomaly)}
            subValue={expectedRangeSubValue(expected)}
            info={expectedRangeInfo(expected, anomaly, latestCurrent)}
          />
          <TrendStat
            label="Reporting PDUs"
            value={`${reportingPduCount}/${expectedPduCount}`}
            subValue={`${reportingPercent}% reporting`}
          />
          <TrendStat
            label="Zero-current inlets"
            value={formatTrendCount(latest.zeroCurrentInletCount)}
            subValue={`${formatTrendCount(latest.currentReadingCount)} current readings`}
          />
          <TrendStat
            label="Energy used"
            value={hasNumber(meta.measuredEnergyDeltaKwh) ? `${formatNumber(meta.measuredEnergyDeltaKwh, 2)} kWh` : "-"}
            subValue={hasNumber(meta.measuredEnergyDeltaKwh) ? "From PDU kWh counter" : "No kWh counter data"}
            info="Measured from the PDU's cumulative kWh counter over this selected timeframe."
          />
          <TrendStat
            label="Peak active power"
            value={hasNumber(peakActivePower) ? `${formatNumber(peakActivePower, 0)} W` : "-"}
            subValue="Highest bucket total"
            info="Highest total active power observed in one bucket during this selected timeframe."
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            color: "var(--app-text-muted)",
            fontSize: "0.85rem",
            flexWrap: "wrap",
            marginBottom: "0.75rem",
          }}
        >
          <span>{formatTrendDateRange(since, before)}</span>
          <span>{bucketMinutes} minute buckets</span>
        </div>
        <div style={{ minHeight: "285px", position: "relative" }}>
          {loading ? (
            <ChartLoadingPanel dark={dark} />
          ) : (
            <Line options={chartOptions} data={chartData} />
          )}
          {!loading && loaded && !error && rows.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--app-text-muted)",
              }}
            >
              No trend data for this range.
            </div>
          )}
        </div>
        {error && (
          <div style={{ color: "var(--danger-color)", fontSize: "0.9rem", marginTop: "0.75rem" }}>
            {error}
          </div>
        )}
      </div>
    </AppCard>
  );
};

const TrendStat = ({ label, value, subValue, color = "var(--app-text)", info }) => (
  <div style={{ borderTop: "1px solid var(--app-border)", paddingTop: "0.75rem" }}>
    <div
      style={{
        alignItems: "center",
        color: "var(--app-text-muted)",
        display: "flex",
        fontSize: "0.78rem",
        fontWeight: 600,
        gap: "0.15rem",
      }}
    >
      <span>{label}</span>
      {info ? <InfoPopover text={info} /> : null}
    </div>
    <div style={{ color, fontWeight: 700, fontSize: "1.2rem", marginTop: "0.25rem" }}>
      {value}
    </div>
    {subValue && (
      <div style={{ color: "var(--app-text-muted)", fontSize: "0.82rem", marginTop: "0.2rem" }}>
        {subValue}
      </div>
    )}
  </div>
);

const visibleInlets = (inlets = []) => {
  const activeOrNonAts = inlets.filter((inlet) => inlet.atsInletActive || !inlet.atsInletId);
  return activeOrNonAts.length ? activeOrNonAts : inlets;
};

const hasAnyReading = (inlets, field) =>
  visibleInlets(inlets).some((inlet) => hasNumber(inlet[field]));

const InletTableRow = ({ item, parentProps }) => {
  const { columnWidths, inletsLoading, inletsLoaded } = parentProps;
  const inlets = Array.isArray(item.inlets) ? item.inlets : [item].filter(Boolean);
  const firstInlet = inlets[0] || {};
  const displayInlets = visibleInlets(inlets);
  const connected = firstInlet.connectionState === "Connected";
  const showSinglePhaseReadings = firstInlet.inletPhase !== "Three Phase";
  const showVoltageReadings = showSinglePhaseReadings && hasAnyReading(inlets, "inletVoltageRms");
  const showLineFrequencyReadings = showSinglePhaseReadings && hasAnyReading(inlets, "inletLineFrequency");
  const showPowerFactorReadings = showSinglePhaseReadings && hasAnyReading(inlets, "inletPowerFactor");
  const showActivePowerReadings = showSinglePhaseReadings && hasAnyReading(inlets, "inletActivePower");
  const showEnergyReadings = hasAnyReading(inlets, "inletEnergyAccumulation");

  return (
    <div
      key={item.enclosureSerialNumber}
      className={`grid-row-wrapper ${connected ? "" : "disconnected"}`}
      title={connected ? "" : "Disconnected"}
    >
      <div className={`grid-row ${connected ? "connected" : "disconnected"}`}>
        <div style={{ flex: columnWidths[2] }}>
          {inletsLoading ? (
            <div className="skeleton-blank" />
          ) : (
            <Link to={`/devices/connected/${item.enclosureSerialNumber}/inlets`}>
              <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                  textDecoration: "underline",
                  paddingLeft: "10px"
                }}
              >
                <span style={{ fontWeight: "600" }}>
                  {{
                    single: "Standard Single Inlet",
                    ats: "Auto Transfer Switch (ATS)",
                    dual: "Dual Inlet/Circuit",
                  }[firstInlet.inletType] || "-"}
                </span>
                <span>
                  {firstInlet.inletPlug || "-"}
                  {firstInlet.inletType === "dual" && " x 2"}
                </span>
              </span>
            </Link>
          )}
        </div>
        <div style={{ flex: columnWidths[3] }}>
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              {firstInlet.deviceName === "SynLink PDU" || !firstInlet.deviceName ? (
                <span>PDU SN: {firstInlet.enclosureSerialNumber}</span>
              ) : (
                firstInlet.deviceName
              )}
            </span>
          </span>
        </div>
        <div style={{ flex: columnWidths[4] }}>
          <GridColumn
            loading={inletsLoading}
            loaded={inletsLoaded}
            connected={connected}
            component={
              firstInlet.inletPhase === "Three Phase" ? (
                firstInlet.inletLines?.length ? (
                  <DataValues
                    dataValues={(firstInlet.inletLines || []).map((line) => ({
                      value: line.currentRms || 0,
                      unit: `A (${line.line})`,
                    }))}
                  />
                ) : "-"
              ) : (
                <DataValues
                  dataValues={displayInlets.map((inlet) => ({
                    value: inlet.inletCurrentRms || 0,
                    unit: "A",
                  }))}
                />
              )
            }
          />
        </div>
        <div style={{ flex: columnWidths[5] }}>
          <GridColumn
            loading={inletsLoading || item.loading}
            loaded={inletsLoaded}
            connected={connected}
            component={
              showVoltageReadings ? (
                <DataValues
                  dataValues={displayInlets.map((inlet) => ({
                    value: inlet.inletVoltageRms || 0,
                    unit: "V",
                  }))}
                />
              ) : "-"
            }
          />
        </div>
        <div style={{ flex: columnWidths[6] }}>
          <GridColumn
            loading={inletsLoading || item.loading}
            loaded={inletsLoaded}
            connected={connected}
            component={
              showLineFrequencyReadings ? (
                <DataValues
                  dataValues={displayInlets.map((inlet) => ({
                    value: inlet.inletLineFrequency || 0,
                    unit: "Hz",
                  }))}
                />
              ) : "-"
            }
          />
        </div>
        <div style={{ flex: columnWidths[7] }}>
          <GridColumn
            loading={inletsLoading || item.loading}
            loaded={inletsLoaded}
            connected={connected}
            component={
              showPowerFactorReadings ? (
                <DataValues
                  dataValues={displayInlets.map((inlet) => ({
                    value: inlet.inletPowerFactor || 0,
                    unit: "",
                  }))}
                />
              ) : "-"
            }
          />
        </div>
        <div style={{ flex: columnWidths[8] }}>
          <GridColumn
            loading={inletsLoading || item.loading}
            loaded={inletsLoaded}
            connected={connected}
            component={
              showActivePowerReadings ? (
                <DataValues
                  dataValues={displayInlets.map((inlet) => ({
                    value: inlet.inletActivePower || 0,
                    unit: "W",
                  }))}
                />
              ) : "-"
            }
          />
        </div>
        <div style={{ flex: columnWidths[9] }}>
          <GridColumn
            loading={inletsLoading}
            loaded={inletsLoaded}
            connected={connected}
            component={
              showEnergyReadings ? (
                <DataValues
                  dataValues={displayInlets.map((inlet) => ({
                    value: formatNumber(inlet.inletEnergyAccumulation, 2),
                    unit: "kWh",
                  }))}
                />
              ) : "-"
            }
          />
        </div>
        <div style={{ flex: columnWidths[11] }} />
      </div>
    </div>
  );
};

class InletGroupForm extends React.Component {
  state = {
    groupName: "",
  };
  submitGroup = () => {
    this.props.createResource(
      "groups",
      {
        name: this.state.groupName,
        groupType: "devices",
        items: this.props.items,
      },
      (err, newGroup) => {
        if (newGroup) {
          this.props.changeAppState({
            selectedGroup: this.state.groupName,
            selectedGroupId: newGroup.id,
          });
          this.props.close();
        } else {
          console.log(err, newGroup);
        }
      }
    );
  };
  render() {
    return (
      <div>
        <TextInput
          label="Group Name"
          callback={(value) => {
            this.setState({ groupName: value });
          }}
          value={this.state.groupName}
          type="text"
          error=""
          placeholder="Example Name"
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: "30px",
            paddingBottom: "-30px",
          }}
        >
          <div
            className="button"
            style={{
              backgroundColor: "rgba(0,0,0,0.4)",
            }}
            onClick={() => this.props.close()}
          >
            Cancel
          </div>
          <div
            className="button"
            onClick={() => {
              this.submitGroup();
            }}
          >
            Submit
          </div>
        </div>
      </div>
    );
  }
}

const GridColumn = ({ loading, loaded, connected, component }) => {
  // if (!loaded && loading) {
  if (loading) {
    return <div className="skeleton-blank" />;
  } else if (loaded && !connected) {
    return "-";
  } else {
    return component;
  }
};

const DataValue = ({ value, unit }) => {
  return (
    <span style={{ display: "flex", alignItems: "flex-end" }}>
      <span
        style={{
          fontWeight: "600",
          fontSize: "105%",
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: "80%",
          paddingLeft: "4px",
          paddingBottom: "2px",
        }}
      >
        {unit}
      </span>
    </span>
  );
};

const DataValues = ({ dataValues }) => {
  return (
    <span style={{ display: "flex", flexDirection: "column" }}>
      {dataValues.map((i, y) => (
        <span key={y}>
          <DataValue
            value={typeof i.value === "string" ? i.value : i.value.toFixed(2)}
            unit={i.unit}
          />
        </span>
      ))}
    </span>
  );
};

class PaginatedList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPageNumber: 1,
      pageSize: 10,
    };
  }

  componentDidUpdate = (prevProps) => {
    if (this.props.resetKey !== prevProps.resetKey) {
      this.setState({ currentPageNumber: 1 });
    }
  }

  findPageInCache = (pageNumber) => {
    const { pages } = this.props;
    if (!Array.isArray(pages)) return null;
    return pages.find((p) => p.page === pageNumber);
  };

  handlePageClick = ({ selected }) => {
    const newPage = selected + 1;
    this.setState({ currentPageNumber: newPage }, () => {
      this.fetchPageIfNeeded(newPage);
      this.fetchPageIfNeeded(newPage + 1);
    });
  };

  fetchPageIfNeeded = (pageNumber) => {
    if (pageNumber < 1) return;
    if (this.findPageInCache(pageNumber)) return;
    if (this.props.pageLoading?.[pageNumber]) return;
    this.fetchPage(pageNumber);
  };

  fetchPage = (pageNumber) => {
    const { fetchResourcePaginate, resourceName, queryParams = {} } = this.props;
    fetchResourcePaginate(
      resourceName,
      undefined,
      {
        page: pageNumber,
        limit: this.state.pageSize,
        ...queryParams,
      }
    );
  };

  getCurrentPageItems = () => {
    const currentPageObj = this.findPageInCache(this.state.currentPageNumber);
    return currentPageObj ? currentPageObj[this.props.resourceName] : [];
  };

  render() {
    const {
      totalItemCount,
      ItemRenderer,
      pageLoading = {},
      LoadingProp,
      showSummary,
      itemLabel,
      renderHeader,
    } = this.props;
    const { currentPageNumber, pageSize } = this.state;
    const totalPages = totalItemCount ? Math.ceil(totalItemCount / pageSize) : 1;
    const currentPageItems = this.getCurrentPageItems();
    const showSpinner = !!pageLoading[currentPageNumber] && currentPageItems.length === 0;
    const hasTotalCount = typeof totalItemCount === "number";
    const resourceLabel = itemLabel || this.props.resourceName || "items";
    let summaryText = null;

    if (showSummary && hasTotalCount && !showSpinner) {
      if (totalItemCount === 0) {
        summaryText = `Showing 0 of 0 ${resourceLabel}`;
      } else if (currentPageItems.length === 0) {
        summaryText = `Showing 0 of ${totalItemCount} ${resourceLabel}`;
      } else {
        const showingStart = (currentPageNumber - 1) * pageSize + 1;
        const showingEnd = Math.min(showingStart + currentPageItems.length - 1, totalItemCount);
        summaryText = `Showing ${showingStart}-${showingEnd} of ${totalItemCount} ${resourceLabel}`;
      }
    }

    return (
      <div>
        {summaryText && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              color: "#6b7280",
              fontSize: "14px",
              marginBottom: "8px",
            }}
          >
            {summaryText}
          </div>
        )}
        {typeof renderHeader === "function" ? renderHeader() : null}

        {showSpinner && (
          LoadingProp ? (
            <LoadingProp />
          ) : (
            <div style={{ margin: "16rem 0" }}>
              <Loading black />
            </div>
          )
        )}

        {!showSpinner &&
          currentPageItems.map((item, i) => (
            <ItemRenderer
              key={item.id || item.enclosureSerialNumber || i}
              item={item}
              i={i}
              parentProps={this.props.parentProps}
            />
          ))}

        {totalPages > 1 && (
          <div style={{ marginTop: "2rem" }}>
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              forcePage={currentPageNumber - 1}
              pageCount={totalPages}
              onPageChange={this.handlePageClick}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              containerClassName="react-paginate"
              activeClassName="selected"
            />
          </div>
        )}
      </div>
    );
  }
}

const GroupInputs = ({
  updateState,
  setActiveInletGroup,

  editingGroupHasChanges,
  editingActive,

  groups,
  createGroup,
  removeGroup,
  updateGroup,

  selectedGroup,
  selectedGroupId,
  selectedDevices,
}) => {
  return (
    <div
      style={
        {
          // display: "flex",
          // justifyContent: "space-between",
          // marginBottom: "10px",
        }
      }
      className="grid-x "
    >
      <div style={{}} className="cell large-6">
        <SelectInput
          label=""
          callback={(result) => {
            setActiveInletGroup(parseInt(result, 10));
          }}
          value={selectedGroupId}
          options={groups.map((g) => ({
            label: g.name,
            value: g.id,
          }))}
          inputContainerStyle={{
            margin: "0px",
          }}
          inputStyle={{
            backgroundColor: "var(--app-control-bg)",
            color: "var(--app-control-text)",
            fontWeight: "600",
          }}
          defaultValue="Select A Group"
        />
      </div>
      {selectedDevices.length > 0 && !selectedGroup && (
        <div className="cell large-6">
          <div
            className="button"
            onClick={() => {
              createGroup();
            }}
          >
            Create New Group ({selectedDevices.length} selected)
          </div>
        </div>
      )}

      {editingActive && (
        <div className="cell">
          <div // edit group buttons
            style={{
              display: "flex",
              // border: "solid green 1px"
            }}
            className="grid-x "
          >
            <div
              className=" cell"
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="button"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.1)",
                    color: "rgba(0,0,0,0.9)",
                    marginRight: "5px",
                  }}
                  onClick={() => {
                    const currentGroup = groups.find(
                      (g) => g.id === selectedGroupId
                    ) || { items: [] };
                    updateState({
                      editingActive: false,
                      // selectedDevices: currentGroup.items.map(i => ({ enclosureSerialNumber: i }))
                    });
                    setActiveInletGroup(parseInt(currentGroup.id, 10));
                  }}
                >
                  Cancel Edit
                </div>

                <div
                  className="button"
                  style={{
                    backgroundColor: "rgb(140, 56, 56)",
                    // marginLeft: "5px",
                  }}
                  onClick={() => {
                    removeGroup(selectedGroupId);
                  }}
                >
                  Remove Group
                </div>
              </div>
              {editingGroupHasChanges && (
                <div
                  className="button"
                  style={{
                    // backgroundColor: '#49535E',
                    backgroundColor: "var(--info-color)",
                    // color: 'rgba(0,0,0,0.9)',
                    color: "white",
                    marginLeft: "5px",
                  }}
                  onClick={() => {
                    updateGroup(selectedGroupId);
                  }}
                >
                  Save Changes
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedGroup && !editingActive && (
        <div className="cell">
          <div // selected group buttons
            style={
              {
                // display: 'flex',
                // justifyContent: 'space-between',
                // flexWrap: 'wrap'
                // border: "solid blue 1px"
              }
            }
            className="grid-x grid-padding-y"
          >
            <div className="cell large-12 large-order-1 medium-order-2 small-order-2">
              <div
                className="button"
                style={{
                  backgroundColor: "var(--app-surface-3)",
                  color: "var(--app-text)",
                }}
                onClick={() => {
                  updateState({
                    selectedGroup: "",
                    selectedGroupId: "",
                    selectedDevices: [],
                    editingActive: false,
                  });
                }}
              >
                Unselect Group
              </div>
              <div
                className="button"
                style={{
                  // backgroundColor: '#49535E',
                  backgroundColor: "#49535E",
                  // color: 'rgba(0,0,0,0.9)',
                  color: "white",
                  marginLeft: "5px",
                }}
                onClick={() => {
                  updateState({
                    editingActive: true,
                  });
                }}
              >
                Edit Group
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const INLET_KPI_COLORS = {
  current: "#2563eb",
  power: "#047857",
  energy: "#7c3aed",
  quality: "#b45309",
  neutral: "#64748b",
};

const KpiTile = ({ label, value, meta, accentColor = INLET_KPI_COLORS.neutral, loading }) => (
  <div className="app-kpi-card">
    {loading ? (
      <React.Fragment>
        <div className="skeleton-blank" style={{ width: "55%", height: "30px", marginBottom: "12px" }} />
        <div className="skeleton-blank" style={{ width: "72%", height: "14px", marginBottom: "8px" }} />
        <div className="skeleton-blank" style={{ width: "48%", height: "12px" }} />
      </React.Fragment>
    ) : (
      <React.Fragment>
        <div className="app-kpi-card__value">{value}</div>
        <div className="app-kpi-card__label">{label}</div>
        {meta ? <div className="app-kpi-card__meta">{meta}</div> : null}
      </React.Fragment>
    )}
    <div className="app-kpi-card__accent" style={{ background: accentColor }} />
  </div>
);

const Chartz = ({
  summary = {},
  loading,
  loaded,
}) => {
  const showLoadingState = loading && !loaded;
  const totalKwh = formatNumber(summary.totalEnergyAccumulation || 0, 2);
  const totalCurrentRms = formatNumber(summary.totalCurrentRms || 0, 3);
  const totalWattage = formatNumber(summary.totalActivePower || 0, 3);
  const averagePowerFactor = formatNumber(summary.averagePowerFactor || 0, 3);
  const currentCoverage = `${summary.currentReadingCount || 0}/${summary.inletCount || 0}`;
  const tiles = [
    {
      label: "Total consumption",
      value: `${totalKwh} kWh`,
      meta: `${summary.energyReadingCount || 0} inlets reporting energy`,
      accentColor: INLET_KPI_COLORS.energy,
    },
    {
      label: "Total current RMS",
      value: `${totalCurrentRms} A`,
      meta: `${summary.currentReadingCount || 0} inlets reporting current`,
      accentColor: INLET_KPI_COLORS.current,
    },
    {
      label: "Active power",
      value: `${totalWattage} W`,
      meta: `${summary.activePowerReadingCount || 0} inlets reporting watts`,
      accentColor: INLET_KPI_COLORS.power,
    },
    {
      label: "Avg power factor",
      value: averagePowerFactor,
      meta: `${summary.powerFactorReadingCount || 0} inlets reporting power factor`,
      accentColor: INLET_KPI_COLORS.quality,
    },
    {
      label: "Current coverage",
      value: currentCoverage,
      meta: `${summary.connectedPduCount || 0} connected PDUs`,
      accentColor: INLET_KPI_COLORS.neutral,
    },
  ];

  return (
    <section className="app-kpi-grid" style={{ marginTop: "30px", marginBottom: "30px" }}>
      {tiles.map((tile) => (
        <KpiTile key={tile.label} {...tile} loading={showLoadingState} />
      ))}
    </section>
  );
};

const LoadingSection = ({ columnWidths }) => {
  return (
    <div style={{}}>
      {[null, null, null, null].map((item, index) => {
        return (
          <div className="grid-row-wrapper" key={index}>
            <div className="grid-row">
              <div style={{ flex: columnWidths[0] }}></div>
              <div style={{ flex: columnWidths[1] }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnWidths[2] }}>
                <div className="skeleton-blank" />{" "}
              </div>
              <div style={{ flex: columnWidths[3] }}>
                <div className="skeleton-blank" />{" "}
              </div>
              <div style={{ flex: columnWidths[4] }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnWidths[5] }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnWidths[6] }}>
                <div className="skeleton-blank" />{" "}
              </div>
              <div style={{ flex: columnWidths[7] }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnWidths[8] }}>
                <div className="skeleton-blank" />
              </div>
              <div style={{ flex: columnWidths[9] }}>
                <div className="skeleton-blank" />{" "}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
